/* Compare page — Nutramilo vs similar digital-twin and multi-omics platforms.
 *
 * Objective comparison with Twin Health, Virta Health, InsideTracker,
 * Function Health и Zoe — платформи, които работят в същата ниша.
 * Educational framing, без диагностичен език.
 */
import React, { useState } from "react";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import { SubpageNav, SubpageBackLink, SubpageFooter } from "@/components/SubpageLayout";
import { Check, X, Minus, ArrowRight, Scale, Sparkles } from "lucide-react";

// ─── Competitors: digital-twin / multi-omics health platforms ────────────
const COMPETITORS = [
  {
    id: "twin-health",
    name: "Twin Health",
    tagline: {
      bg: "Дигитален близнак при диабет тип 2 (САЩ / Индия)",
      en: "Whole-Body Digital Twin for type-2 diabetes (US / India)",
      es: "Gemelo digital para diabetes tipo 2 (EE. UU. / India)",
    },
    users: {
      bg: "Над 10 000 пациенти · с медицински надзор",
      en: "10,000+ patients · physician-supervised programme",
      es: "Más de 10 000 pacientes · con supervisión médica",
    },
    price: {
      bg: "≈ $600/месец програмен пакет (често през работодател)",
      en: "≈ $600/mo bundled programme (often via employer)",
      es: "≈ 600 $/mes como paquete (habitualmente a través del empleador)",
    },
    strengths: {
      bg: [
        "Непрекъснат глюкозен сензор + смарт везна + тракер за активност",
        "Специалисти — лекар, диетолог и треньор — през приложението",
        "Фокусиран подход за ремисия на диабет тип 2",
      ],
      en: [
        "Continuous glucose sensor + smart scale + activity tracker",
        "Clinical team — physician, dietitian and coach — in app",
        "Focused protocol for type-2 diabetes remission",
      ],
      es: [
        "Sensor continuo de glucosa + báscula inteligente + pulsera de actividad",
        "Equipo clínico — médico, nutricionista y coach — en la app",
        "Protocolo centrado en la remisión de la diabetes tipo 2",
      ],
    },
    weaknesses: {
      bg: [
        "Само за хора с установен диабет тип 2 — не за превенция",
        "Висока месечна цена и необходимост от сензор и везна",
        "Няма програма за здраве на гърдата, кожа, зъби или наследствен риск",
        "Няма образователен слой за разчитане на лабораторни бланки",
      ],
      en: [
        "Only for people with established type-2 diabetes — not prevention",
        "High monthly cost; requires a sensor and smart scale",
        "No breast, skin, dental or hereditary-risk programmes",
        "No educational layer for reading your own lab results",
      ],
      es: [
        "Solo para personas con diabetes tipo 2 — no para prevención",
        "Coste mensual alto y necesidad de sensor y báscula",
        "Sin programas de salud mamaria, piel, dental o riesgo hereditario",
        "Sin capa educativa para interpretar sus análisis",
      ],
    },
  },
  {
    id: "virta-health",
    name: "Virta Health",
    tagline: {
      bg: "Клиничен дигитален близнак за диабет и затлъстяване",
      en: "Clinical digital twin for diabetes and obesity (US)",
      es: "Gemelo digital clínico para diabetes y obesidad (EE. UU.)",
    },
    users: {
      bg: "Над 100 000 участници · 10+ години публикувани резултати",
      en: "100,000+ participants · 10+ years of published outcomes",
      es: "Más de 100 000 participantes · más de 10 años de resultados publicados",
    },
    price: {
      bg: "През работодател или здравен план (без крайна цена)",
      en: "Covered by employer or health plan (no retail price)",
      es: "A través del empleador o plan sanitario (sin precio al público)",
    },
    strengths: {
      bg: [
        "Доказана програма с кето-ориентиран подход и клиничен екип",
        "Проследяване на кетони и глюкоза вкъщи",
        "Обзор от лекар, който променя терапията при нужда",
      ],
      en: [
        "Evidence-backed ketogenic programme with a clinical team",
        "At-home ketone and glucose monitoring",
        "Physician review that can adjust medication",
      ],
      es: [
        "Programa basado en evidencia con enfoque cetogénico y equipo clínico",
        "Monitorización domiciliaria de cetonas y glucosa",
        "Revisión médica que ajusta la medicación cuando procede",
      ],
    },
    weaknesses: {
      bg: [
        "Един хранителен модел — не работи за всички метаболитни профили",
        "Няма симулатор на чинията — предписва се диетичен план",
        "Няма образователен слой на български; достъпна е само в САЩ",
        "Не покрива ранно откриване или скрининг на кожа и гърда",
      ],
      en: [
        "One dietary pattern — doesn't fit every metabolic profile",
        "No plate simulator — a prescribed diet instead",
        "No Bulgarian educational layer; US-only access",
        "No early-detection or skin / breast screening",
      ],
      es: [
        "Un único patrón alimentario — no se ajusta a todos los perfiles metabólicos",
        "Sin simulador del plato — se prescribe una dieta",
        "Sin capa educativa en español; solo disponible en EE. UU.",
        "No cubre cribado temprano, piel ni mama",
      ],
    },
  },
  {
    id: "insidetracker",
    name: "InsideTracker",
    tagline: {
      bg: "Платформа за биомаркери и мулти-омикс",
      en: "Biomarker and multi-omics health platform",
      es: "Plataforma de biomarcadores y multi-ómica",
    },
    users: {
      bg: "Над 400 000 потребители · университетски произход (MIT)",
      en: "400,000+ users · spun out of MIT",
      es: "Más de 400 000 usuarios · surgida del MIT",
    },
    price: {
      bg: "Пакети от около $200 до $500 за комплект; абонамент около $25/мес",
      en: "Panels from about $200 to $500; subscription around $25/mo",
      es: "Paneles desde unos 200 $ a 500 $; suscripción desde unos 25 $/mes",
    },
    strengths: {
      bg: [
        "Голям брой проследявани биомаркери в кръв",
        "Генетичен модул, който сверява препоръки спрямо гените",
        "Биологична възраст и персонални цели за всеки показател",
      ],
      en: [
        "Wide range of blood biomarkers tracked",
        "Genetic module that aligns recommendations with your genes",
        "Biological age and personal target range per marker",
      ],
      es: [
        "Amplia gama de biomarcadores sanguíneos",
        "Módulo genético que ajusta las recomendaciones a sus genes",
        "Edad biológica y objetivos personales por marcador",
      ],
    },
    weaknesses: {
      bg: [
        "Няма симулатор на чинията и дневник на храненето в реално време",
        "Няма мрежа от дигитални близнаци на 11 системи",
        "Няма модул за здраве на гърдата или фото скрининг на кожа",
        "Търсенето на кръвни маркери изисква отделна лаборатория в САЩ",
      ],
      en: [
        "No plate simulator or real-time food diary",
        "No 11-system digital-twin network",
        "No breast-health module or skin photo screening",
        "Blood-marker intake requires a separate US-based lab",
      ],
      es: [
        "Sin simulador del plato ni diario de alimentación en tiempo real",
        "Sin red de gemelos digitales de 11 sistemas",
        "Sin módulo de salud mamaria ni cribado fotográfico de piel",
        "La extracción de biomarcadores requiere un laboratorio aparte en EE. UU.",
      ],
    },
  },
  {
    id: "function-health",
    name: "Function Health",
    tagline: {
      bg: "Голям годишен лабораторен пакет с обяснения",
      en: "Comprehensive annual lab subscription",
      es: "Suscripción anual de analíticas integrales",
    },
    users: {
      bg: "Над 100 000 потребители · подкрепена от публични лица",
      en: "100,000+ users · high-profile backing",
      es: "Más de 100 000 usuarios · con respaldo de figuras públicas",
    },
    price: {
      bg: "$499 годишно (над 100 показателя, 2 пакета)",
      en: "$499 per year (100+ markers, 2 panels)",
      es: "499 $ al año (más de 100 marcadores, 2 paneles)",
    },
    strengths: {
      bg: [
        "Над 100 кръвни показателя, обяснени на разбираем език",
        "Два пакета годишно за динамика на показателите",
        "Клиничен преглед от лекар при тревожни стойности",
      ],
      en: [
        "100+ blood markers explained in plain language",
        "Two panels per year to track trends",
        "Physician review for concerning values",
      ],
      es: [
        "Más de 100 marcadores sanguíneos explicados con claridad",
        "Dos paneles al año para seguir tendencias",
        "Revisión médica cuando aparecen valores preocupantes",
      ],
    },
    weaknesses: {
      bg: [
        "Няма дневник на храненето и симулатор на ястия",
        "Няма проследяване на инсулиновия товар на всяко хранене",
        "Няма образователни модули за здраве на гърдата, кожа или зъби",
        "Достъпна само в САЩ; само на английски",
      ],
      en: [
        "No food diary or plate simulator",
        "No per-meal insulin-load tracking",
        "No educational modules for breast, skin or dental health",
        "US-only access; English-only interface",
      ],
      es: [
        "Sin diario de alimentación ni simulador de platos",
        "Sin seguimiento de la carga insulínica por comida",
        "Sin módulos educativos de salud mamaria, piel o dental",
        "Solo disponible en EE. UU.; interfaz solo en inglés",
      ],
    },
  },
  {
    id: "zoe",
    name: "Zoe",
    tagline: {
      bg: "Мулти-омикс: микробиом + сензор за глюкоза + мазнини (UK)",
      en: "Multi-omics: microbiome + CGM + bloodfats (UK)",
      es: "Multi-ómica: microbioma + sensor de glucosa + lípidos (RU)",
    },
    users: {
      bg: "Над 150 000 потребители · проф. Тим Спектор (King's College)",
      en: "150,000+ users · Prof. Tim Spector (King's College)",
      es: "Más de 150 000 usuarios · prof. Tim Spector (King's College)",
    },
    price: {
      bg: "≈ $299 стартов комплект + $60/месец абонамент",
      en: "≈ $299 start kit + $60/mo subscription",
      es: "≈ 299 $ de kit inicial + 60 $/mes de suscripción",
    },
    strengths: {
      bg: [
        "Тест на микробиома с физически комплект",
        "Непрекъснат глюкозен сензор първите две седмици",
        "Персонални резултати от голямото PREDICT проучване",
      ],
      en: [
        "Microbiome test with a physical kit",
        "Continuous glucose monitor for the first 2 weeks",
        "Personal scores from the large PREDICT study",
      ],
      es: [
        "Prueba de microbioma con kit físico",
        "Sensor continuo de glucosa durante las primeras 2 semanas",
        "Puntuaciones personales del gran estudio PREDICT",
      ],
    },
    weaknesses: {
      bg: [
        "Висока цена и зависимост от hardware — трудно достъпно в България",
        "Фокус върху един резултат — няма мрежа от 11 системи",
        "Няма разчитане на лабораторни бланки от Вашата лаборатория",
        "Няма модул за здраве на гърдата, кожа или зъби",
      ],
      en: [
        "High cost and hardware dependence — hard to use in Bulgaria",
        "Focused on a single score — no 11-system network",
        "No reading of lab PDFs from your own lab",
        "No breast, skin or dental modules",
      ],
      es: [
        "Precio elevado y dependencia de hardware — difícil de usar en Bulgaria",
        "Centrado en una sola puntuación — sin red de 11 sistemas",
        "Sin lectura de los informes de su propio laboratorio",
        "Sin módulos de mama, piel ni dental",
      ],
    },
  },
];

// ─── Feature matrix — relevant to digital-twin / multi-omics space ───────
const FEATURE_ROWS = [
  {
    category: { bg: "Данни, с които работи", en: "Data it works with", es: "Datos con los que trabaja" },
    items: [
      {
        label: { bg: "Качване на бланка от Вашата лаборатория", en: "Upload a PDF from your own lab", es: "Subir un informe de su propio laboratorio" },
        n: "yes", "twin-health": "no", "virta-health": "partial", insidetracker: "partial", "function-health": "no", zoe: "no",
      },
      {
        label: { bg: "Разпознаване на ястие по снимка", en: "Meal recognition from a photo", es: "Reconocimiento de comida por foto" },
        n: "yes", "twin-health": "no", "virta-health": "no", insidetracker: "no", "function-health": "no", zoe: "yes",
      },
      {
        label: { bg: "Записване на хранене с глас", en: "Voice meal logging", es: "Registro de comidas por voz" },
        n: "yes", "twin-health": "no", "virta-health": "no", insidetracker: "no", "function-health": "no", zoe: "no",
      },
      {
        label: { bg: "Непрекъснат глюкозен сензор", en: "Continuous glucose sensor", es: "Sensor continuo de glucosa" },
        n: "partial", "twin-health": "yes", "virta-health": "yes", insidetracker: "no", "function-health": "no", zoe: "yes",
      },
      {
        label: { bg: "Тест на микробиома", en: "Microbiome test", es: "Prueba de microbioma" },
        n: "no", "twin-health": "no", "virta-health": "no", insidetracker: "partial", "function-health": "partial", zoe: "yes",
      },
      {
        label: { bg: "Анализ на наследствеността", en: "Hereditary-risk analysis", es: "Análisis de riesgo hereditario" },
        n: "yes", "twin-health": "no", "virta-health": "no", insidetracker: "yes", "function-health": "no", zoe: "no",
      },
    ],
  },
  {
    category: { bg: "Дигитални близнаци и системи", en: "Digital twins and systems", es: "Gemelos digitales y sistemas" },
    items: [
      {
        label: { bg: "Мрежа от 11 физиологични системи", en: "Network of 11 physiological systems", es: "Red de 11 sistemas fisiológicos" },
        n: "yes", "twin-health": "partial", "virta-health": "partial", insidetracker: "no", "function-health": "no", zoe: "no",
      },
      {
        label: { bg: "Симулатор на чинията преди хранене", en: "Pre-meal plate simulator", es: "Simulador del plato antes de comer" },
        n: "yes", "twin-health": "no", "virta-health": "no", insidetracker: "no", "function-health": "no", zoe: "no",
      },
      {
        label: { bg: "Инсулинов товар за всяко хранене", en: "Per-meal insulin load", es: "Carga insulínica por comida" },
        n: "yes", "twin-health": "partial", "virta-health": "partial", insidetracker: "no", "function-health": "no", zoe: "partial",
      },
      {
        label: { bg: "Биологична възраст", en: "Biological age", es: "Edad biológica" },
        n: "yes", "twin-health": "no", "virta-health": "no", insidetracker: "yes", "function-health": "partial", zoe: "partial",
      },
      {
        label: { bg: "Висцерален мастен индекс (без скенер)", en: "Visceral fat index (no scanner)", es: "Índice de grasa visceral (sin escáner)" },
        n: "yes", "twin-health": "no", "virta-health": "no", insidetracker: "no", "function-health": "no", zoe: "no",
      },
    ],
  },
  {
    category: { bg: "Скрининг и ранно откриване", en: "Screening and early detection", es: "Cribado y detección temprana" },
    items: [
      {
        label: { bg: "Програма за здраве на гърдата", en: "Breast health programme", es: "Programa de salud mamaria" },
        n: "yes", "twin-health": "no", "virta-health": "no", insidetracker: "no", "function-health": "no", zoe: "no",
      },
      {
        label: { bg: "Фото оценка на кожна лезия", en: "Skin lesion photo assessment", es: "Evaluación fotográfica de lesiones cutáneas" },
        n: "yes", "twin-health": "no", "virta-health": "no", insidetracker: "no", "function-health": "no", zoe: "no",
      },
      {
        label: { bg: "Стоматологичен скрининг", en: "Dental screening", es: "Cribado dental" },
        n: "no", "twin-health": "no", "virta-health": "no", insidetracker: "no", "function-health": "no", zoe: "no",
      },
    ],
  },
  {
    category: { bg: "Клиничен слой", en: "Clinical layer", es: "Capa clínica" },
    items: [
      {
        label: { bg: "Разчитане на лабораторни показатели на разбираем език", en: "Lab marker interpretation in plain language", es: "Interpretación de marcadores con lenguaje claro" },
        n: "yes", "twin-health": "no", "virta-health": "partial", insidetracker: "yes", "function-health": "yes", zoe: "partial",
      },
      {
        label: { bg: "Отчет за принтиране за Вашия лекар", en: "Printable report for your doctor", es: "Informe imprimible para su médico" },
        n: "yes", "twin-health": "no", "virta-health": "partial", insidetracker: "partial", "function-health": "partial", zoe: "no",
      },
      {
        label: { bg: "Клинична бележка с електронен подпис (за лекари)", en: "Signed clinical note (for doctors)", es: "Nota clínica firmada (para médicos)" },
        n: "yes", "twin-health": "no", "virta-health": "no", insidetracker: "no", "function-health": "no", zoe: "no",
      },
    ],
  },
  {
    category: { bg: "Достъпност и език", en: "Accessibility and language", es: "Accesibilidad e idioma" },
    items: [
      {
        label: { bg: "Български интерфейс", en: "Bulgarian interface", es: "Interfaz en búlgaro" },
        n: "yes", "twin-health": "no", "virta-health": "no", insidetracker: "no", "function-health": "no", zoe: "no",
      },
      {
        label: { bg: "Испански интерфейс", en: "Spanish interface", es: "Interfaz en español" },
        n: "yes", "twin-health": "no", "virta-health": "no", insidetracker: "no", "function-health": "no", zoe: "no",
      },
      {
        label: { bg: "Работи без hardware и без изпращане в чужбина", en: "Works without hardware or shipping abroad", es: "Funciona sin hardware ni envíos al extranjero" },
        n: "yes", "twin-health": "no", "virta-health": "no", insidetracker: "no", "function-health": "no", zoe: "no",
      },
    ],
  },
  {
    category: { bg: "Цена", en: "Pricing", es: "Precio" },
    items: [
      {
        label: { bg: "Безплатен план", en: "Free plan", es: "Plan gratuito" },
        n: "yes", "twin-health": "no", "virta-health": "no", insidetracker: "no", "function-health": "no", zoe: "no",
      },
      {
        label: {
          bg: "Основен платен план (месец)",
          en: "Main paid plan (month)",
          es: "Plan de pago principal (mes)",
        },
        n: "€19.90", "twin-health": "≈ $600", "virta-health": "N/A", insidetracker: "≈ $25", "function-health": "≈ $42", zoe: "≈ $60",
      },
      {
        label: { bg: "Нужен е физически комплект или сензор", en: "Physical kit or sensor required", es: "Se requiere kit físico o sensor" },
        n: "no", "twin-health": "yes", "virta-health": "yes", insidetracker: "yes", "function-health": "yes", zoe: "yes",
      },
    ],
  },
];

// ─── Cell renderer ──────────────────────────────────────────────────────
function Cell({ value, isNutramilo }) {
  if (value === "yes") {
    return (
      <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full ${isNutramilo ? "bg-emerald-500 text-white" : "bg-emerald-100 text-emerald-700"}`}>
        <Check className="w-3.5 h-3.5" strokeWidth={3} />
      </span>
    );
  }
  if (value === "no") {
    return (
      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-rose-100 text-rose-400">
        <X className="w-3.5 h-3.5" strokeWidth={2.5} />
      </span>
    );
  }
  if (value === "partial") {
    return (
      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-amber-100 text-amber-700">
        <Minus className="w-3.5 h-3.5" strokeWidth={3} />
      </span>
    );
  }
  return (
    <span className={`text-xs font-medium ${isNutramilo ? "text-emerald-700" : "text-gray-600"}`}>
      {value}
    </span>
  );
}

// ─── Main page ──────────────────────────────────────────────────────────
export default function ComparePage() {
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const L = (bgT, enT, esT) => (language === "bg" ? bgT : language === "es" ? (esT ?? enT) : enT);
  const [activeTab, setActiveTab] = useState(COMPETITORS[0].id);

  const active = COMPETITORS.find((c) => c.id === activeTab) || COMPETITORS[0];
  const pick = (obj) => obj?.[language] ?? obj?.en ?? obj?.bg;

  return (
    <div className={`min-h-screen ${isDark ? "bg-[hsl(223,47%,5%)]" : "bg-gradient-to-b from-slate-50 to-white"}`}>
      <SEO
        title={L(
          "Сравнение: Nutramilo и други дигитални близнаци и мулти-омикс платформи",
          "Compare: Nutramilo vs other digital-twin and multi-omics platforms",
          "Comparación: Nutramilo frente a otras plataformas de gemelos digitales y multi-ómica"
        )}
        description={L(
          "Обективно сравнение на Nutramilo с Twin Health, Virta Health, InsideTracker, Function Health и Zoe.",
          "Objective comparison of Nutramilo with Twin Health, Virta Health, InsideTracker, Function Health and Zoe.",
          "Comparación objetiva de Nutramilo con Twin Health, Virta Health, InsideTracker, Function Health y Zoe."
        )}
        path="/compare"
      />
      <SubpageNav />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <SubpageBackLink />

        <div className="mt-4 mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-semibold mb-3">
            <Scale className="w-3.5 h-3.5" />
            {L("Обективно сравнение", "Objective comparison", "Comparación objetiva")}
          </div>
          <h1 className={`text-3xl sm:text-4xl font-bold mb-2 ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
            {L(
              "Nutramilo и подобните платформи",
              "Nutramilo and similar platforms",
              "Nutramilo y las plataformas similares"
            )}
          </h1>
          <p className={`text-sm sm:text-base ${isDark ? "text-gray-400" : "text-gray-600"}`}>
            {L(
              "Сравняваме се с други платформи за дигитални близнаци и мулти-омикс здраве — Twin Health, Virta Health, InsideTracker, Function Health и Zoe. Данните са от официалните им сайтове (януари 2026). Nutramilo остава образователен инструмент и не заменя лекар.",
              "We compare with other digital-twin and multi-omics health platforms — Twin Health, Virta Health, InsideTracker, Function Health and Zoe. Data from their official sites (January 2026). Nutramilo remains an educational tool and does not replace a doctor.",
              "Nos comparamos con otras plataformas de gemelos digitales y multi-ómica — Twin Health, Virta Health, InsideTracker, Function Health y Zoe. Datos de sus sitios oficiales (enero de 2026). Nutramilo sigue siendo una herramienta educativa y no sustituye a un médico."
            )}
          </p>
        </div>

        {/* ─── Tabs ─── */}
        <div className="flex flex-wrap gap-2 mb-6" data-testid="compare-tabs">
          {COMPETITORS.map((c) => (
            <button
              key={c.id}
              onClick={() => setActiveTab(c.id)}
              data-testid={`compare-tab-${c.id}`}
              className={`px-4 py-2 rounded-full text-xs font-semibold transition-colors ${
                activeTab === c.id
                  ? "bg-emerald-500 text-white"
                  : isDark
                  ? "bg-slate-800 text-slate-300 hover:bg-slate-700"
                  : "bg-slate-100 text-slate-700 hover:bg-slate-200"
              }`}
            >
              {c.name}
            </button>
          ))}
        </div>

        {/* ─── Active competitor summary ─── */}
        <div className={`grid md:grid-cols-2 gap-4 mb-10 rounded-2xl p-5 border ${isDark ? "border-slate-800 bg-slate-900/60" : "border-slate-200 bg-white"}`}>
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs font-bold uppercase tracking-wider text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded">
                {active.name}
              </span>
              <span className={`text-xs ${isDark ? "text-slate-500" : "text-slate-400"}`}>· {pick(active.tagline)}</span>
            </div>
            <p className={`text-xs mb-2 ${isDark ? "text-slate-400" : "text-slate-600"}`}>
              {pick(active.users)}
            </p>
            <p className={`text-xs font-semibold ${isDark ? "text-slate-300" : "text-slate-700"}`}>
              {pick(active.price)}
            </p>
            <div className="mt-4">
              <p className={`text-[11px] font-bold uppercase tracking-wider mb-1.5 ${isDark ? "text-emerald-300" : "text-emerald-700"}`}>
                {L("Силни страни", "Strengths", "Puntos fuertes")}
              </p>
              <ul className={`text-xs space-y-1 ${isDark ? "text-slate-300" : "text-slate-700"}`}>
                {pick(active.strengths).map((s, i) => (
                  <li key={i} className="flex gap-1.5"><span className="text-emerald-500 shrink-0">+</span>{s}</li>
                ))}
              </ul>
            </div>
          </div>
          <div>
            <p className={`text-[11px] font-bold uppercase tracking-wider mb-1.5 mt-9 md:mt-0 ${isDark ? "text-rose-300" : "text-rose-600"}`}>
              {L("Къде Nutramilo предлага повече", "Where Nutramilo offers more", "Dónde Nutramilo ofrece más")}
            </p>
            <ul className={`text-xs space-y-1 ${isDark ? "text-slate-300" : "text-slate-700"}`}>
              {pick(active.weaknesses).map((w, i) => (
                <li key={i} className="flex gap-1.5"><span className="text-rose-500 shrink-0">·</span>{w}</li>
              ))}
            </ul>
          </div>
        </div>

        {/* ─── Feature matrix ─── */}
        <div className={`rounded-2xl border overflow-hidden ${isDark ? "border-slate-800 bg-slate-900/60" : "border-slate-200 bg-white"}`}>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className={`${isDark ? "bg-slate-800/60" : "bg-slate-50"}`}>
                  <th className={`text-left px-3 py-3 font-semibold ${isDark ? "text-slate-200" : "text-slate-700"}`}>
                    {L("Функция", "Feature", "Función")}
                  </th>
                  <th className="text-center px-2 py-3 font-bold text-emerald-700">Nutramilo</th>
                  {COMPETITORS.map((c) => (
                    <th
                      key={c.id}
                      className={`text-center px-2 py-3 font-semibold ${isDark ? "text-slate-400" : "text-slate-500"}`}
                    >
                      {c.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {FEATURE_ROWS.map((grp) => (
                  <React.Fragment key={pick(grp.category)}>
                    <tr>
                      <td
                        colSpan={2 + COMPETITORS.length}
                        className={`px-3 pt-4 pb-1 text-[10px] font-bold uppercase tracking-wider ${isDark ? "text-slate-500" : "text-slate-400"}`}
                      >
                        {pick(grp.category)}
                      </td>
                    </tr>
                    {grp.items.map((row, i) => (
                      <tr
                        key={pick(row.label) + i}
                        className={`${isDark ? "border-t border-slate-800" : "border-t border-slate-100"}`}
                      >
                        <td className={`px-3 py-2.5 ${isDark ? "text-slate-200" : "text-slate-700"}`}>
                          {pick(row.label)}
                        </td>
                        <td className="text-center px-2 py-2.5">
                          <Cell value={row.n} isNutramilo />
                        </td>
                        {COMPETITORS.map((c) => (
                          <td key={c.id} className="text-center px-2 py-2.5">
                            <Cell value={row[c.id]} />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* ─── Legend ─── */}
        <div className="mt-4 flex flex-wrap gap-4 text-xs">
          <span className="inline-flex items-center gap-1.5"><span className="w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700"><Check className="w-3 h-3" strokeWidth={3} /></span>{L("Да", "Yes", "Sí")}</span>
          <span className="inline-flex items-center gap-1.5"><span className="w-5 h-5 rounded-full bg-amber-100 flex items-center justify-center text-amber-700"><Minus className="w-3 h-3" strokeWidth={3} /></span>{L("Частично", "Partial", "Parcial")}</span>
          <span className="inline-flex items-center gap-1.5"><span className="w-5 h-5 rounded-full bg-rose-100 flex items-center justify-center text-rose-400"><X className="w-3 h-3" strokeWidth={2.5} /></span>{L("Не", "No", "No")}</span>
        </div>

        {/* ─── CTA ─── */}
        <div className={`mt-10 rounded-2xl p-6 border ${isDark ? "border-slate-800 bg-gradient-to-br from-emerald-900/20 to-slate-900/60" : "border-slate-200 bg-gradient-to-br from-emerald-50 to-white"}`}>
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="w-4 h-4 text-emerald-600" />
            <span className={`text-xs font-bold uppercase tracking-wider ${isDark ? "text-emerald-300" : "text-emerald-700"}`}>
              {L("Защо Nutramilo", "Why Nutramilo", "Por qué Nutramilo")}
            </span>
          </div>
          <h2 className={`text-xl sm:text-2xl font-bold mb-3 ${isDark ? "text-white" : "text-slate-900"}`}>
            {L(
              "Същата дълбочина без скъп комплект и чужда лаборатория.",
              "The same depth without an expensive kit or a foreign lab.",
              "La misma profundidad sin un kit caro ni un laboratorio extranjero."
            )}
          </h2>
          <p className={`text-sm mb-5 max-w-2xl ${isDark ? "text-slate-300" : "text-slate-700"}`}>
            {L(
              "Nutramilo обединява дневника на храненето, разчитането на лабораторни бланки, мрежата от 11 дигитални близнаци и програмата за здраве на гърдата в един профил — на български, английски и испански.",
              "Nutramilo brings together a food diary, lab result interpretation, an 11-system digital-twin network and a breast-health programme in a single account — in Bulgarian, English and Spanish.",
              "Nutramilo reúne diario de alimentación, interpretación de análisis, una red de 11 gemelos digitales y un programa de salud mamaria en una sola cuenta — en búlgaro, inglés y español."
            )}
          </p>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/register"
              data-testid="compare-cta-register"
              className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold"
            >
              {L("Започнете безплатно", "Start free", "Empezar gratis")} <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/pricing"
              className={`inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full text-sm font-semibold ${
                isDark ? "border border-slate-700 text-slate-200 hover:bg-slate-800" : "border border-slate-200 text-slate-700 hover:bg-slate-50"
              }`}
            >
              {L("Вижте плановете", "See the plans", "Ver los planes")}
            </Link>
          </div>
        </div>
      </div>

      <SubpageFooter />
    </div>
  );
}
